import { useState } from 'react'
import { Badge, Button, Checkbox } from '@/components/ds2'
import type { FleetLoad } from '../../api/assignDriverApi'
import {
    CANDIDATES_SHOWN,
    OVERDUE_CHOICES,
    clockOf,
    deliveryPassedWordsOf,
    homeTimeWordsOf,
    milesWords,
    pickupHeaderOf,
    readyWordsOf,
    visibleCandidates,
    type AssignCandidate,
    type OverdueConfirm,
    type OverdueDelivery,
} from './assignModel'

interface AssignDriverPickerProps {
    /** The server's trucks for the search, in its order; undefined while they are read. */
    candidates: AssignCandidate[] | undefined
    error: boolean
    /** The search box's text; the server filters on it. */
    query: string
    onQueryChange: (query: string) => void
    /** Puts the load on the chosen truck. `dispatch` is the "Also dispatch" box; `confirm` the overdue choice, when the delivery has passed. */
    onAssign: (candidate: AssignCandidate, dispatch: boolean, confirm?: OverdueConfirm) => void
    onClose: () => void
    /** The load the picker is for: its header and whether its pickup has passed. */
    load?: FleetLoad
    /** The row's passed delivery date. The load's own answer (`delivery_passed`) is used when this is not given. */
    overdue?: OverdueDelivery
}

/**
 * The status of a row: home time, an inactive driver, or when the truck is free. Once the pickup has passed only the ready time is shown;
 * before it, a truck not free in time shows its ready time in warning, never "Ready now" beside a pickup flag.
 */
const StatusWords = ({ candidate, pickupPassed }: { candidate: AssignCandidate; pickupPassed: boolean }) => {
    if (candidate.inactive) return <Badge variant="soft" type="warning" size="sm" label="Driver inactive" />
    if (candidate.off) return <span className="truncate text-foreground-muted">{homeTimeWordsOf(candidate.off)}</span>
    if (!pickupPassed && candidate.readyBeforePickup === false)
        return <span className="truncate text-warning">{candidate.readyAt ? `Ready ${clockOf(candidate.readyAt)}` : 'Ready time unknown'}</span>
    return <span className="truncate text-foreground-muted">{readyWordsOf(candidate.readyAt) ?? 'Ready time unknown'}</span>
}

/** The driver names: a team reads both; a truck with no driver says so. */
const driversWordsOf = (candidate: AssignCandidate): string => (candidate.noDriver ? 'No driver' : candidate.drivers.join(' · '))

/**
 * The trucks for one load, under its row: the first few the server ranks, the search for any other, one Assign per truck. The list is
 * the server's; the search is sent to it. "Also dispatch" starts off, so an assign never messages a driver unless the user asks for it.
 */
export const AssignDriverPicker = ({ candidates, error, query, onQueryChange, onAssign, onClose, load, overdue }: AssignDriverPickerProps) => {
    const [showAll, setShowAll] = useState(false)
    const [dispatch, setDispatch] = useState(false)
    // The overdue choice: sent with the truck's Assign request. A passed delivery takes a choice before any truck.
    const [choice, setChoice] = useState<OverdueConfirm>()
    const all = candidates ?? []
    // A search shows every match the server sent; without one, the first few and "Show all".
    const searching = query.trim().length > 0
    const shown = searching ? all : visibleCandidates(all, showAll)
    const pickupPassed = load?.pickup_passed === true
    const late: OverdueDelivery | undefined = overdue ?? (load?.delivery_passed ? { at: load.delivery_at ?? null, timezone: load.timezone } : undefined)
    // The counts read the same list the picker draws, so they cannot disagree with it.
    const noLocation = all.filter((candidate) => !candidate.hasCoords).length
    const inactive = all.filter((candidate) => candidate.inactive).length
    const countLine = [
        `${all.length} ${all.length === 1 ? 'truck' : 'trucks'}`,
        `${noLocation} ${noLocation === 1 ? 'has' : 'have'} no location`,
        inactive > 0 ? `${inactive} ${inactive === 1 ? 'driver' : 'drivers'} inactive` : '',
    ]
        .filter(Boolean)
        .join(' · ')
    const noneLocated = all.length > 0 && noLocation === all.length
    const offersAll = !searching && !showAll && all.length > CANDIDATES_SHOWN
    const assign = (candidate: AssignCandidate) => (choice ? onAssign(candidate, dispatch, choice) : onAssign(candidate, dispatch))

    return (
        <div role="region" className="flex flex-col gap-2 rounded-xl bg-default p-3 text-xs text-foreground" aria-label="Pick a truck">
            <p className="font-medium">{load ? pickupHeaderOf(load) : 'Pick a truck'}</p>
            {late && (
                <div role="group" aria-label="Overdue delivery" className="flex flex-col gap-2">
                    <p role="status" className="rounded-xl bg-warning-soft p-2 text-warning">
                        {`${deliveryPassedWordsOf(late.at, late.timezone)}. Choose how to handle it before you pick a truck.`}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {OVERDUE_CHOICES.map((option) => (
                            <Button
                                key={option.confirm}
                                size="sm"
                                variant={choice === option.confirm ? 'primary' : 'outline'}
                                aria-pressed={choice === option.confirm}
                                onClick={() => setChoice(option.confirm)}
                            >
                                {option.label}
                            </Button>
                        ))}
                    </div>
                </div>
            )}
            {pickupPassed && !late && (
                <p role="status" className="rounded-xl bg-warning-soft p-2 text-warning">
                    Pickup has passed. Ready times are shown; distance is still measured.
                </p>
            )}
            <input
                type="search"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                placeholder="Search any truck or driver"
                aria-label="Search trucks and drivers"
                className="h-8 w-full rounded-xl bg-surface px-3 text-xs text-foreground shadow-field placeholder:text-foreground-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
            {candidates === undefined && !error && <p className="py-2 text-foreground-muted">Reading the trucks…</p>}
            {error && (
                <p role="alert" className="py-2 text-danger">
                    The trucks could not be read.
                </p>
            )}
            {candidates !== undefined && all.length === 0 && (
                <p className="py-2 text-foreground-muted">{searching ? 'No truck matches the search.' : 'No truck is free before this pickup.'}</p>
            )}
            {all.length > 0 && (
                <p className="flex flex-wrap items-center gap-x-2 text-foreground-muted">
                    <span>{countLine}</span>
                    {offersAll && (
                        <Button size="sm" variant="ghost" onClick={() => setShowAll(true)}>
                            Show all
                        </Button>
                    )}
                </p>
            )}
            {noneLocated && <p className="text-foreground-muted">Distances unknown: no truck has a location</p>}
            <ul className="flex flex-col">
                {shown.map((candidate) => (
                    <li key={candidate.truckId} className="flex items-center gap-3 border-t border-border py-2 first:border-t-0">
                        <span className="min-w-0 flex-1">
                            <span className="flex min-w-0 items-center gap-2">
                                <span className="shrink-0 font-medium">{`Truck ${candidate.truckLabel}`}</span>
                                <span className="min-w-0 truncate">
                                    <StatusWords candidate={candidate} pickupPassed={pickupPassed} />
                                </span>
                            </span>
                            <span className="block truncate text-foreground-muted">{driversWordsOf(candidate)}</span>
                        </span>
                        <span className="flex w-40 shrink-0 flex-col items-end tabular-nums text-foreground-muted">
                            <span title={candidate.deadheadMiles === null ? 'No location for this truck' : undefined}>
                                {milesWords(candidate.deadheadMiles)}
                            </span>
                            <span
                                title={candidate.locationSource}
                                className={`block max-w-full truncate ${candidate.locationMuted ? 'text-foreground-muted' : ''}`}
                            >
                                {candidate.location}
                            </span>
                        </span>
                        <Button
                            size="sm"
                            variant="secondary"
                            aria-label={`Assign truck ${candidate.truckLabel}`}
                            disabled={!!late && !choice}
                            onClick={() => assign(candidate)}
                        >
                            Assign
                        </Button>
                    </li>
                ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3 border-t border-border pt-2">
                <span className="min-w-0 flex-1">
                    <Checkbox
                        title="Also dispatch to the driver (Telegram)"
                        ariaLabel="Also dispatch to the driver"
                        checked={dispatch}
                        onChange={setDispatch}
                    />
                </span>
                {/* The user is already picking a truck here, so the way out is "Close"; "Pick another truck" belongs to a question or refusal. */}
                <Button size="sm" variant="ghost" onClick={onClose}>
                    Close
                </Button>
            </div>
        </div>
    )
}
